/**
 * Binary Data Utilities for Patchwerk
 * 
 * Provides compression, encoding, and share-string generation for patch data.
 * Uses browser-native CompressionStream (deflate) for efficient storage.
 * 
 * Share string format: PW1~<base64-data>~<crc32-checksum>
 * - PW1: Version identifier (Patchwerk v1)
 * - base64-data: URL-safe base64 encoded compressed data
 * - crc32-checksum: 8-char hex CRC32 of the compressed bytes
 */

/**
 * CRC32 lookup table (IEEE polynomial)
 */
const CRC32_TABLE = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    CRC32_TABLE[i] = c >>> 0;
}

/**
 * Calculate CRC32 checksum of a Uint8Array
 * @param {Uint8Array} bytes 
 * @returns {string} 8-character hex string
 */
export function crc32(bytes) {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) {
        crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    }
    return ((crc ^ 0xFFFFFFFF) >>> 0).toString(16).padStart(8, '0');
}

/**
 * Compress an object to a Uint8Array using deflate
 * @param {Object} obj - Object to compress
 * @returns {Promise<Uint8Array>} Compressed bytes
 */
export async function compress(obj) {
    const json = JSON.stringify(obj);
    const encoder = new TextEncoder();
    const input = encoder.encode(json);
    
    // Use CompressionStream if available (modern browsers)
    if (typeof CompressionStream !== 'undefined') {
        const cs = new CompressionStream('deflate');
        const writer = cs.writable.getWriter();
        const reader = cs.readable.getReader();
        
        // Write input
        writer.write(input);
        writer.close();
        
        // Read compressed output
        const chunks = [];
        let totalLength = 0;
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            totalLength += value.length;
        }
        
        // Concatenate chunks
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.length;
        }
        return result;
    }
    
    // Fallback: no compression (just return UTF-8 encoded JSON)
    console.warn('CompressionStream not available, using uncompressed storage');
    return input;
}

/**
 * Decompress a Uint8Array back to an object
 * @param {Uint8Array} bytes - Compressed bytes
 * @returns {Promise<Object>} Decompressed object
 */
export async function decompress(bytes) {
    // Use DecompressionStream if available
    if (typeof DecompressionStream !== 'undefined') {
        try {
            const ds = new DecompressionStream('deflate');
            const writer = ds.writable.getWriter();
            const reader = ds.readable.getReader();
            
            // Write compressed input
            writer.write(bytes);
            writer.close();
            
            // Read decompressed output
            const chunks = [];
            let totalLength = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                totalLength += value.length;
            }
            
            // Concatenate chunks
            const result = new Uint8Array(totalLength);
            let offset = 0;
            for (const chunk of chunks) {
                result.set(chunk, offset);
                offset += chunk.length;
            }
            
            const decoder = new TextDecoder();
            const json = decoder.decode(result);
            return JSON.parse(json);
        } catch (e) {
            // If decompression fails, try parsing as uncompressed JSON
            console.warn('Decompression failed, trying as plain JSON:', e.message);
        }
    }
    
    // Fallback: try parsing as uncompressed UTF-8 JSON
    const decoder = new TextDecoder();
    const json = decoder.decode(bytes);
    return JSON.parse(json);
}

/**
 * Convert Uint8Array to URL-safe base64
 * @param {Uint8Array} bytes 
 * @returns {string}
 */
export function bytesToBase64(bytes) {
    // Convert to regular base64
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    
    // Make URL-safe: replace + with -, / with _, remove padding =
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Convert URL-safe base64 to Uint8Array
 * @param {string} str 
 * @returns {Uint8Array}
 */
export function base64ToBytes(str) {
    // Restore standard base64: replace - with +, _ with /
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
        base64 += '=';
    }
    
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/**
 * Convert an object to a human-friendly share string with checksum
 * Format: PW1~<base64-data>~<crc32>
 * 
 * @param {Object} obj - Object to encode
 * @returns {Promise<string>} Share string
 */
export async function toShareString(obj) {
    const compressed = await compress(obj);
    const base64 = bytesToBase64(compressed);
    const checksum = crc32(compressed);
    return `PW1~${base64}~${checksum}`;
}

/**
 * Parse a share string back to an object
 * Validates checksum and throws on mismatch
 * 
 * @param {string} str - Share string
 * @returns {Promise<Object>} Decoded object
 * @throws {Error} If checksum validation fails or format is invalid
 */
export async function fromShareString(str) {
    // Trim whitespace
    str = str.trim();
    
    // Parse format
    const parts = str.split('~');
    if (parts.length !== 3) {
        throw new Error('Invalid share string format: expected PW1~data~checksum');
    }
    
    const [version, base64, checksum] = parts;
    
    // Version check
    if (version !== 'PW1') {
        throw new Error(`Unknown share string version: ${version}`);
    }
    
    // Decode base64 to bytes
    let compressed;
    try {
        compressed = base64ToBytes(base64);
    } catch (e) {
        throw new Error('Invalid share string: corrupted data encoding');
    }
    
    // Verify checksum
    const actualChecksum = crc32(compressed);
    if (actualChecksum !== checksum.toLowerCase()) {
        throw new Error(`Checksum mismatch: expected ${checksum}, got ${actualChecksum}. Data may be incomplete or corrupted.`);
    }
    
    // Decompress and parse
    try {
        return await decompress(compressed);
    } catch (e) {
        throw new Error(`Failed to decode share string: ${e.message}`);
    }
}

/**
 * Convert Uint8Array to hex string (for database storage inspection)
 * @param {Uint8Array} bytes 
 * @returns {string}
 */
export function bytesToHex(bytes) {
    return Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Convert hex string to Uint8Array
 * @param {string} hex 
 * @returns {Uint8Array}
 */
export function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return bytes;
}

/**
 * Compute SHA-256 hash of data
 * @param {Uint8Array|string} data - Data to hash
 * @returns {Promise<string>} Hex-encoded hash
 */
export async function sha256(data) {
    let bytes;
    if (typeof data === 'string') {
        bytes = new TextEncoder().encode(data);
    } else {
        bytes = data;
    }
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
    const hashArray = new Uint8Array(hashBuffer);
    return bytesToHex(hashArray);
}

/**
 * Compute content hash for an object (for deduplication)
 * Compresses the object and returns SHA-256 hash of compressed bytes
 * @param {Object} obj - Object to hash
 * @returns {Promise<string>} Hex-encoded SHA-256 hash
 */
export async function computeContentHash(obj) {
    const compressed = await compress(obj);
    return sha256(compressed);
}

/**
 * Encode object for Supabase binary storage
 * Returns hex-encoded string with \x prefix for BYTEA column
 * 
 * PostgreSQL BYTEA format: \x followed by hex characters
 * This ensures proper binary storage and consistent retrieval.
 * 
 * @param {Object} obj - Object to encode
 * @returns {Promise<string>} Hex encoded compressed data with \x prefix
 */
export async function encodeForStorage(obj) {
    const compressed = await compress(obj);
    // Use PostgreSQL hex format for BYTEA
    return '\\x' + bytesToHex(compressed);
}

/**
 * Decode object from Supabase binary storage
 * 
 * Handles multiple formats:
 * - PostgreSQL hex format: \x followed by hex chars (Supabase default for BYTEA)
 * - URL-safe base64: - instead of +, _ instead of / (our encoding format)
 * - Standard base64: + and / with optional = padding
 * - Raw Uint8Array
 * 
 * @param {string|Uint8Array} data - Hex, base64 string, or raw bytes from BYTEA
 * @returns {Promise<Object>} Decoded object
 */
export async function decodeFromStorage(data) {
    let bytes;
    if (typeof data === 'string') {
        // Check for PostgreSQL hex format (starts with \x)
        if (data.startsWith('\\x')) {
            // Strip \x prefix and decode hex
            const hex = data.slice(2);
            bytes = hexToBytes(hex);
        } else if (/^[0-9a-fA-F]+$/.test(data) && data.length % 2 === 0 && data.length > 20) {
            // Plain hex without \x prefix (all hex chars, even length, reasonable size)
            bytes = hexToBytes(data);
        } else {
            // Assume base64 (URL-safe or standard)
            bytes = base64ToBytes(data);
        }
    } else if (data instanceof Uint8Array) {
        bytes = data;
    } else {
        throw new Error('Invalid storage data type');
    }
    return decompress(bytes);
}
